import { Info, History } from 'lucide-react';
import { DatePopover } from '@/components/ui/date-popover';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface PassageDateDisplayProps {
    passageDate?: DateProposal;
    alternativeDates?: DateProposal[];
    className?: string;
    showThrob?: boolean;
    variant?: 'default' | 'compact';
    overrideDisplayDate?: string;
}

export function PassageDateDisplay({
    passageDate,
    alternativeDates,
    className,
    showThrob,
    variant = 'default',
    overrideDisplayDate
}: PassageDateDisplayProps) {
    const isDarkMode = useDarkMode();

    if (!passageDate && !overrideDisplayDate) return null;

    if (variant === 'compact') {
        return (
            <div className={cn("inline-flex items-center", className)}>
                <DatePopover
                    date={passageDate!}
                    alternativeDates={alternativeDates}
                    title="Passage Date"
                >
                    <button
                        className={cn(
                            "group relative flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-colors cursor-pointer",
                            "hover:bg-white/10 active:bg-white/20",
                            isDarkMode ? "text-white/90" : "text-slate-900"
                        )}
                    >
                        {showThrob && (
                            <div className="absolute inset-0 bg-white opacity-0 animate-throb-glow z-0 rounded-md pointer-events-none" />
                        )}
                        <span className="text-[13px] font-bold whitespace-nowrap tabular-nums tracking-tight relative z-10">
                            {overrideDisplayDate || passageDate?.displayDate}
                        </span>
                    </button>
                </DatePopover>
            </div>
        );
    }

    return (
        <div className={cn("flex flex-col items-center", className)}>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <DatePopover
                    date={passageDate}
                    alternativeDates={alternativeDates}
                    title="Passage Date"
                >
                    <button
                        className={cn(
                            "group relative flex flex-col items-center gap-0.5 px-6 py-2.5 rounded-xl border-2 transition-all duration-300 cursor-pointer",
                            "shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-md",
                            isDarkMode
                                ? "bg-[#2a2520] border-[#6b5d4f] text-[#d4c5b3]"
                                : "bg-[#fdfbf7] border-[#a89575] text-[#5c4a33]"
                        )}
                        style={{
                            boxShadow: isDarkMode
                                ? '0 10px 25px -5px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)'
                                : '0 10px 25px -5px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
                        }}
                    >
                        {/* Throb feedback on arrival (synced with 1.5s ghost flight delay) */}
                        {showThrob && (
                            <div className="absolute inset-[-4px] bg-white opacity-0 animate-throb-glow z-0 rounded-2xl pointer-events-none" />
                        )}

                        <div className="flex items-center gap-1.5 relative z-10">
                            <History className={cn(
                                "h-3 w-3 transition-transform group-hover:scale-110",
                                isDarkMode ? "text-primary/40" : "text-primary/60"
                            )} />
                            <span className={cn(
                                "text-[10px] font-bold uppercase tracking-[0.25em]",
                                isDarkMode ? "text-primary/40" : "text-primary/60"
                            )}>
                                Passage Date
                            </span>
                        </div>

                        <div className="flex items-center gap-2 relative z-10">
                            <span className="text-2xl md:text-3xl font-serif font-bold tracking-tight">
                                {passageDate.displayDate}
                            </span>
                            <div className={cn(
                                "p-1 rounded-full transition-colors",
                                isDarkMode ? "bg-primary/5 group-hover:bg-primary/10" : "bg-primary/10 group-hover:bg-primary/20"
                            )}>
                                <Info className={cn(
                                    "h-3.5 w-3.5 transition-colors",
                                    isDarkMode ? "text-muted-foreground group-hover:text-foreground" : "text-primary/70 group-hover:text-primary"
                                )} />
                            </div>
                        </div>
                    </button>
                </DatePopover>
            </motion.div>
        </div>
    );
}

